import * as fs from 'fs'
import path from 'path'
import { fileURLToPath } from 'url'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type SitemapEntry } from '@nasa-gcn/remix-seo/build/types'
import { AnimatePresence, motion } from 'motion/react'
import {
	type DetailedHTMLProps,
	Fragment,
	type HTMLAttributes,
	memo,
	type ReactNode,
	type Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
	data,
	Link,
	type LoaderFunctionArgs,
	Outlet,
	redirect,
	useLoaderData,
	useMatches,
	useRouteLoaderData,
	useSearchParams,
} from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import Logo4dCeramics from '#app/components/logo-4d-ceramics.tsx'
import * as LogoFull from '#app/components/logo-full.js'
import * as LogoIcon from '#app/components/logo.js'
import Banner from '#app/components/marketing/banner.tsx'
import ImageJumbo, {
	ImageJumboContent,
	ImageJumboContent2,
} from '#app/components/marketing/image-jumbo.tsx'
import { ProductLink } from '#app/components/marketing/product-link.tsx'
import { TeaserVideoJumbo } from '#app/components/marketing/teaser-video-jumbo.tsx'
import { VideoJumbo } from '#app/components/marketing/video-jumbo.tsx'
import ProductJumbo from '#app/components/products/product-jumbo.tsx'
import { Button } from '#app/components/ui/button.js'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.js'
import { Icon } from '#app/components/ui/icon.js'
import { Text } from '#app/components/ui/typography.tsx'
import { NewsletterForm } from '#app/features/newsletter/form.tsx'
import { useOnClickOutside } from '#app/hooks/use-click-outside.js'
import { useIntersectionObserver } from '#app/hooks/use-intersection-observer.js'
import { useIsMounted } from '#app/hooks/use-is-mounted.ts'
import { useRouteChangeEffect } from '#app/hooks/use-route-change.tsx'
import { CERAMICS_4D_LANDING_PAGE_COLOR } from '#app/routes/($lang)+/_marketing+/4d-ceramics.tsx'
import { useMainStore } from '#app/stores/main'
import { prisma } from '#app/utils/db.server.js'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import {
	i18n,
	type ISupportedLanguage,
	LocalizedLink,
	useLocale,
} from '#app/utils/i18n.js'
import { i18nCookie, i18next } from '#app/utils/i18next.server.js'
import { useIsMoreThanTablet } from '#app/utils/media-query.js'
import { cn, delay } from '#app/utils/misc.js'
import { useOptionalUser } from '#app/utils/user.js'
import routes from '#routes/index.js'
import { PublicContextProvider, usePublicContext } from './__public-context'

// Check if the language from the url is different from the current locale and set the cookie
export async function loader({ request, params }: LoaderFunctionArgs) {
	// Priority 1: URL parameter (if explicitly provided, it should always win)
	// Priority 2: Cookie
	// Priority 3: Browser Accept-Language header
	const detectedLocale = await i18next.getLocale(request)
	// i18next.getLocale() now reads the URL first (see i18next.server.ts), so it
	// no longer tells us whether the *stored* cookie is stale. Read it directly
	// for that comparison.
	const cookieLocale = await i18nCookie.parse(request.headers.get('Cookie'))

	// If there's a language in the URL, use it. Otherwise use detected locale
	const locale = (params.lang || detectedLocale) as ISupportedLanguage

	if (params.lang && !i18n.supportedLngs.includes(params.lang)) {
		throw new Response('Not found', { status: 404 })
	}

	let product

	const isProductDetailRoute = new URL(request.url).pathname.match(
		'products/(.*)',
	)

	if (isProductDetailRoute && isProductDetailRoute.length > 0) {
		const slug = isProductDetailRoute[1]
		product = await prisma.collection.findFirst({
			where: {
				slug,
				translations: {
					some: {
						languageCode: locale,
					},
				},
			},
			include: {
				designsImages: {
					take: 1,
					select: {
						id: true,
						altText: true,
					},
				},
				translations: {
					where: {
						languageCode: locale,
					},
					take: 1,
				},
				characteristics: {
					select: {
						backgroundColor: true,
						textColor: true,
						fourthDimension: true,
					},
				},
			},
		})
	}

	const banner = await prisma.banner.findFirst({
		where: {
			active: true,
			translations: {
				some: {
					languageCode: locale,
				},
			},
		},
		select: {
			id: true,
			translations: {
				select: {
					description: true,
					title: true,
					link: true,
				},
				where: {
					languageCode: locale,
				},
			},
		},
	})

	const typologies = await prisma.collectionTypology.findMany({
		include: {
			translations: {
				where: {
					languageCode: locale,
				},
			},
		},
		where: {
			collections: {
				some: {
					published: true,
				},
			},
		},
	})

	const response = {
		typologies: typologies.map((typology) => ({
			...typology,
			translations: undefined,
			name: typology.translations[0]!.name,
		})),
		banner: {
			id: banner?.id,
			title: banner?.translations[0]?.title,
			description: banner?.translations[0]?.description,
			link: banner?.translations[0]?.link,
		},
		product,
	}

	// If there's a language in the URL (params.lang), it takes precedence
	// We should update the cookie to match it
	if (params.lang) {
		// URL language parameter exists and is valid (already checked above)
		// Always update cookie to match the URL parameter
		if (cookieLocale !== params.lang) {
			return data(response, {
				headers: {
					'set-cookie': await i18nCookie.serialize(params.lang),
				},
			})
		}
	} else {
		// No language in URL - use cookie/browser detection
		const { pathname, search } = new URL(request.url)

		// If detected locale is not the fallback, redirect to include it in URL
		// This ensures language preference persists in the URL
		if (detectedLocale !== i18n.fallbackLng) {
			throw redirect(`/${detectedLocale}${pathname}${search}`)
		}
	}

	return response
}

const __filename = fileURLToPath(import.meta.url)
const __dirname = path.dirname(__filename)

// Sitemap entries are defined here. Put this in the documentation.!
export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => {
		const locales = i18n.supportedLngs
		const basePath = path.resolve(__dirname)
		const routes: SitemapEntry[] = []

		const traverseDirectory = (dir: string, locale: ISupportedLanguage) => {
			const files = fs.readdirSync(dir)

			files.forEach((file) => {
				const fullPath = path.join(dir, file)
				const stat = fs.lstatSync(fullPath)

				if (stat.isDirectory() && !file.startsWith('settings')) {
					traverseDirectory(fullPath, locale)
				} else if (
					stat.isFile() &&
					file.endsWith('.tsx') &&
					!file.includes('.server.') &&
					!file.includes('_layout') &&
					!file.includes('__') &&
					!file.includes('$') &&
					// Exclude private files
					!['me.tsx', 'onboarding.tsx', 'verify.tsx'].includes(file)
				) {
					const relativePath = path.relative(basePath, fullPath)

					const routePath = `($lang)+/${relativePath}`
						.replace(/\.tsx$/, '')
						.replace(/\/index$/, '')
						.replace(/\+/g, '')
						.replace(/\/_.+\//, '/')
						.replace(/\/_.+$/, '')

						.replace(/\(\$lang\)/g, locale)

					routes.push({
						route: `/${routePath}`,
						changefreq: 'daily',
						priority: 0.8,
					})
				}
			})
		}

		locales.forEach((locale) => {
			traverseDirectory(basePath, locale)
		})

		return routes
	}),
}

const SocialLinks = memo(function SocialLinks() {
	return (
		<div className={cn('flex items-center gap-xxs divide-x text-current')}>
			<a
				href={routes.external.social.instagram}
				target="_blank"
				rel="noreferrer noopener"
			>
				<Text
					className={
						'tracking-[0.2em] transition-all duration-200 ease-in-out hover:!font-medium'
					}
					as={'p'}
					size={'small'}
				>
					ig
				</Text>
			</a>
			<a
				href={routes.external.social.linkedin}
				target="_blank"
				rel="noreferrer noopener"
				className={'pl-3'}
			>
				<Text
					className={
						'tracking-[0.2em] transition-all duration-200 ease-in-out hover:!font-medium'
					}
					as={'p'}
					size={'small'}
				>
					li
				</Text>
			</a>
			<a
				href={routes.external.social.pinterest}
				target="_blank"
				rel="noreferrer noopener"
				className={'pl-3'}
			>
				<Text
					className={
						'tracking-[0.2em] transition-all duration-200 ease-in-out hover:!font-medium'
					}
					as={'p'}
					size={'small'}
				>
					pin
				</Text>
			</a>
		</div>
	)
})

function FullWidthContentLayout({
	jumbo,
	children,
	contentRef,
}: {
	jumbo: ReactNode
	children: ReactNode
	contentRef?: Ref<HTMLDivElement>
}) {
	const backgroundColor = useMainStore.use.backgroundColor()

	return (
		<div className="relative z-0 col-span-full col-start-1 row-start-1 row-end-5 grid grid-cols-subgrid grid-rows-subgrid">
			<div
				style={{
					backgroundColor,
				}}
				className={cn(
					'col-span-full row-span-full',
					'fixed inset-0 -z-10 lg:!left-navigation', // Behind everything, fixed position
					'transition-colors duration-1000 ease-in-out',
				)}
				aria-hidden="true"
			/>

			<div className="col-span-full col-start-1 row-start-1 row-end-4">
				{jumbo}
			</div>
			<div
				ref={contentRef}
				className={cn(
					'col-span-full col-start-1 row-span-full row-start-4 grid h-full w-full max-w-full lg:col-start-2',
					`transition-colors duration-1000 ease-in-out`,
				)}
			>
				{children}
			</div>
		</div>
	)
}

function NormalContentLayout({
	disableFalseCenter,
	children,
}: {
	disableFalseCenter?: boolean
	children: ReactNode
}) {
	const backgroundColor = useMainStore.use.backgroundColor()

	return (
		<>
			<div
				style={{
					backgroundColor,
				}}
				className={cn(
					'fixed inset-0 !left-navigation -z-10',
					'transition-colors duration-1000 ease-in-out',
				)}
				aria-hidden="true"
			/>

			<div
				className={cn(
					'max-w-screen z-0 col-span-full col-start-1 row-start-2 row-end-5 grid grid-cols-subgrid grid-rows-subgrid lg:col-start-2',
					`transition-colors duration-1000 ease-in-out`,
				)}
			>
				<div className="col-span-full col-start-1 row-span-full row-start-1 grid h-full w-full">
					<div className={cn(!disableFalseCenter && 'false-center-on-desktop')}>
						{children}
					</div>
				</div>
			</div>
		</>
	)
}

const IS_PROD = process.env.NODE_ENV === 'production'

export default function PublicLayout() {
	const { t } = useTranslation([
		'sapienstone',
		'professional',
		'magazine',
		'contact',
		'downloads',
		'fourthDimension',
		'sustainability',
		'innovation',
		'design',
	])
	const { banner, typologies, product } = useLoaderData<typeof loader>()
	const matches = useMatches()

	// Get splash state from root loader
	const rootData =
		// eslint-disable-next-line @typescript-eslint/consistent-type-imports
		useRouteLoaderData<typeof import('#app/root.tsx').loader>('root')
	const splashSeen = rootData?.splashSeen ?? true

	const changeBackgroundColor = useMainStore.use.changeBackgroundColor()
	const unregisterSection = useMainStore.use.unregisterSection()
	const registerSection = useMainStore.use.registerSection()

	// Inject GTM after splash screen completes in production
	useEffect(() => {
		if (splashSeen && IS_PROD) {
			const gtmId = window.ENV?.GTM_ID
			if (!gtmId) return

			// Check if GTM already loaded
			if (window.dataLayer) return

			// Initialize dataLayer
			window.dataLayer = window.dataLayer || []
			window.dataLayer.push({
				'gtm.start': new Date().getTime(),
				event: 'gtm.js',
			})

			// Inject GTM script
			const script = document.createElement('script')
			script.async = true
			script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
			document.head.appendChild(script)

			// Inject GTM noscript iframe
			const noscript = document.createElement('noscript')
			const iframe = document.createElement('iframe')
			iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
			iframe.height = '0'
			iframe.width = '0'
			iframe.style.display = 'none'
			iframe.style.visibility = 'hidden'
			noscript.appendChild(iframe)
			document.body.insertBefore(noscript, document.body.firstChild)
		}
	}, [splashSeen])

	useRouteChangeEffect(async (location) => {
		// For the 4D Ceramics page we want a default landing page of the dark blue, which is the first section after the image jumbo. To give the effect of continuity, we want that.
		if (
			location.pathname.includes(
				routes.public.landings.fourthDimension.route({}),
			)
		) {
			await delay(400)
			changeBackgroundColor(CERAMICS_4D_LANDING_PAGE_COLOR)
		} else {
			changeBackgroundColor(extendedTheme.colors.background)
		}

		await delay(400)
		window.scroll(0, 0)
	})

	const isOnJumboVideoLayout = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/index',
	)
	const isOnHomepage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/index',
	)
	const isOnProductDetailPage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/products+/$slug',
	)
	const isOnDressCodePage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/dress-code',
	)
	const isOnWhatIsSapienstonePage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/what-is-sapienstone',
	)
	const isOnSustainabilityPage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/sustainability',
	)
	const isOnInnovationPage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/innovation-and-quality',
	)
	const isOn4DCeramics = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/4d-ceramics',
	)
	const isOnContactPage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/contact',
	)
	const isOnDesignPage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/design',
	)
	const isOnDownloadsPage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/downloads',
	)
	const isOnProfessionalPage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/professional',
	)
	const isOnMagazineHomePage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/magazine+/index',
	)
	const isOnMagazineArticle = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/magazine+/$slug',
	)

	const { isIntersecting: isIntersectingFooter, ref: footerRef } =
		useIntersectionObserver({
			threshold: 0.3,
			onChange: (isIntersecting, entry) => {
				if (isIntersecting) {
					// Register/update this section with its current intersection ratio
					registerSection(
						'footer',
						extendedTheme.colors.background,
						entry.intersectionRatio,
					)
				} else {
					// Remove this section from tracking when it's not visible
					if (unregisterSection) unregisterSection('footer')
				}
			},
		})

	const navigationOpacity = useMemo(() => {
		return isIntersectingFooter ? 0 : 1
	}, [isIntersectingFooter])

	const { isIntersecting: isIntersectingContent, ref: contentRef } =
		useIntersectionObserver({
			threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7],
		})

	return (
		<PublicContextProvider typologies={typologies}>
			<Banner {...banner} />

			<div className="relative grid min-h-screen w-screen max-w-[100vw] grid-cols-layout grid-rows-layout-mobile sm:grid-rows-layout-tablet lg:grid-rows-layout">
				<Navigation
					color={
						isIntersectingContent
							? 'black'
							: product
								? (product.characteristics?.textColor as `#${string}`)
								: 'white'
					}
					style={{
						opacity: navigationOpacity,
					}}
					className={cn(
						'opacity-100 transition-[mix-blend-mode,_opacity,_color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] duration-700 ease-in-out',
					)}
				/>

				<div className="z-[90] col-start-1 col-end-3 row-start-1 row-end-2">
					<Header
						theme={
							isOnHomepage ||
							isOnWhatIsSapienstonePage ||
							isOnProfessionalPage ||
							isOnMagazineHomePage ||
							isOnContactPage ||
							isOnDressCodePage ||
							isOnDownloadsPage ||
							isOn4DCeramics
								? 'light'
								: product
									? (product.characteristics?.textColor as `#${string}`)
									: 'dark'
						}
					/>
				</div>

				{isOnJumboVideoLayout ||
				(isOnProductDetailPage && product) ||
				isOnDressCodePage ||
				isOnWhatIsSapienstonePage ||
				isOn4DCeramics ||
				isOnContactPage ||
				isOnProfessionalPage ||
				isOnMagazineHomePage ||
				isOnDownloadsPage ||
				isOnSustainabilityPage ||
				isOnInnovationPage ||
				isOnDesignPage ? (
					<FullWidthContentLayout
						contentRef={contentRef}
						jumbo={
							isOnJumboVideoLayout ? (
								<TeaserVideoJumbo
									isIntersectingContent={isIntersectingContent}
								/>
							) : isOnProductDetailPage && product ? (
								<ProductJumbo
									// @ts-ignore
									product={product}
								/>
							) : isOnDressCodePage ? (
								<VideoJumbo
									srcDesktop={'/videos/dress-code/DRESS CODE H.mp4'}
									srcMobile={'/videos/dress-code/DRESS CODE V.mp4'}
									isIntersectingContent={isIntersectingContent}
								/>
							) : isOnWhatIsSapienstonePage ? (
								<ImageJumbo
									imageClassName={'brightness-[70%] xl:brightness-100'}
									src={'/img/what-is-sapienstone/silver-wave-4d-hcs.webp'}
									srcAvif={'/img/what-is-sapienstone/silver-wave-4d-hcs.avif'}
									alt={'What is Sapienstone Jumbo Header'}
									children={
										<ImageJumboContent
											title={t('sapienstone:title')}
											description={t('sapienstone:description')}
										/>
									}
								/>
							) : isOn4DCeramics ? (
								<ImageJumbo
									imageClassName={'object-[50%,30%]'}
									src={'/img/4D/1-FOTO HADER 4D.webp'}
									alt={'4D Ceramics Jumbo Header'}
									children={
										<ImageJumboContent
											title={<Logo4dCeramics className={'max-w-[240px]'} />}
											description={t('fourthDimension:description')}
										/>
									}
								/>
							) : isOnContactPage ? (
								<ImageJumbo
									imageClassName={'brightness-[0.9]'}
									src={'/img/contact/SAPIENSTONE1418 copia.webp'}
									alt={'Contact Jumbo Header'}
									children={
										<ImageJumboContent
											title={t('contact:title')}
											description={t('contact:description')}
										/>
									}
								/>
							) : isOnProfessionalPage ? (
								<ImageJumbo
									src={'/img/professional/1-FOTOHEADER-PROFESIONAL.webp'}
									alt={'The Fourth Dimension Jumbo Header'}
									imageClassName={'object-[50%,60%] brightness-[0.85]'}
									children={
										<ImageJumboContent
											className={'xl:translate-y-[10%] 2xl:translate-y-[15%]'}
											title={t('professional:title')}
											description={t('professional:description')}
										/>
									}
								/>
							) : isOnMagazineHomePage ? (
								<ImageJumbo
									src={'/img/magazine/breccia-imperiale-hcs.webp'}
									srcAvif={'/img/magazine/breccia-imperiale-hcs.avif'}
									alt={'Sapienstone Magazine Header'}
									children={
										<ImageJumboContent
											title={t('magazine:title')}
											description={t('magazine:description')}
										/>
									}
								/>
							) : isOnDownloadsPage ? (
								<ImageJumbo
									src={'/img/downloads/1_FOTOHEADER-DESCARGAS.webp'}
									alt={'Sapienstone Downloads Header'}
									children={
										<ImageJumboContent
											title={t('downloads:title')}
											description={t('downloads:description')}
										/>
									}
								/>
							) : isOnSustainabilityPage ? (
								<ImageJumbo
									overlay
									imageClassName={'object-[100%,50%] scale-[1.2]'}
									src={'/img/sustainability/1-FOTOHEADER-SOSTENIBILIDAD.webp'}
									alt={'Sustainability Sapienstone'}
									children={
										<ImageJumboContent2
											theme={'dark'}
											title={t('sustainability:hero.title')}
											subtitle={t('sustainability:hero.subtitle')}
										/>
									}
								/>
							) : isOnInnovationPage ? (
								<ImageJumbo
									overlay
									imageClassName={'object-[100%,50%] scale-[1.2]'}
									src={'/img/innovation/1-FOTOHEADER-INNOVATION.png'}
									alt={'Innovation and Quality from Sapienstone'}
									children={
										<ImageJumboContent2
											theme={'dark'}
											className={'max-w-2xl'}
											title={t('innovation:hero.title')}
											subtitle={t('innovation:hero.subtitle')}
										/>
									}
								/>
							) : isOnDesignPage ? (
								<ImageJumbo
									overlay
									imageClassName={'object-[100%,50%] scale-[1.2]'}
									src={'/img/design/1_FOTOHEADER-DISEÑO.webp'}
									alt={'Design from Sapienstone'}
									children={
										<ImageJumboContent2
											theme={'dark'}
											className={'max-w-2xl'}
											title={t('design:hero.title')}
											subtitle={t('design:hero.subtitle')}
										/>
									}
								/>
							) : null
						}
					>
						<Outlet />
					</FullWidthContentLayout>
				) : (
					<NormalContentLayout disableFalseCenter={!!isOnMagazineArticle}>
						<Outlet />
					</NormalContentLayout>
				)}

				<div
					ref={footerRef}
					className="col-span-full col-start-1 row-start-5 row-end-6"
				>
					<Footer />
				</div>
			</div>
		</PublicContextProvider>
	)
}

// TODO: Crear un contexto de página pública donde meter todos estos campos que modifican el comportamiento de la página. Por ejemplo, si se muestra la navegación, si se muestra la ruta, la barra de búsqueda, etc.
function Navigation(
	props: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
		color: 'white' | 'black' | 'adaptive' | `#${string}`
	},
) {
	const { t } = useTranslation('common')
	const [searchParams] = useSearchParams()
	const matches = useMatches()
	const isOnHomepage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/index',
	)
	const isOnProductPage = matches.find(
		(m) => m.id === 'routes/($lang)+/_marketing+/products+/index',
	)
	const ref = useRef(null)

	const {
		navigation: { open, toggle, items, setSelected, selected, route },
	} = usePublicContext()

	const MenuButton = ({ className }: { className?: string }) => (
		<button
			data-testid={'navigation-toggle'}
			type="button"
			aria-label={'Toggle navigation'}
			className={cn(
				'flex items-center justify-center px-sm md:px-lg lg:px-sm',
				className,
			)}
			onClick={() => {
				toggle()
			}}
		>
			<Icon
				name={open ? 'lucide-x' : 'lucide-menu-2'}
				className="h-6 w-6 md:h-7 md:w-7"
			/>
		</button>
	)

	useOnClickOutside(ref, () => {
		if (open) {
			toggle()
		}
	})

	const container = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,

			transition: {
				duration: 0.3,
				staggerChildren: 0.2,
			},
		},
	}

	const listItem = {
		hidden: { opacity: 0 },
		show: { opacity: 1 },
	}

	const mounted = useIsMounted()
	const isAtLeastTablet = useIsMoreThanTablet()

	const isCustomColor = props.color.startsWith('#')

	return (
		<div
			ref={ref}
			{...props}
			className={cn(
				'group z-[100] col-start-1 col-end-2 row-start-1 row-end-5 mix-blend-difference',
				props.className,
				open && '!mix-blend-normal',
				!open && 'pointer-events-none lg:pointer-events-auto',
				!open && props.color === 'black' && '!mix-blend-multiply',
				!open && isCustomColor && '!mix-blend-normal',
			)}
		>
			<div
				className={cn(
					'fixed top-0 h-screen py-lg lg:px-sm',
					props.color === 'white' || props.color === 'adaptive'
						? 'text-white lg:border-r-white'
						: isCustomColor
							? `text-[${props.color}] lg:border-r-[${props.color}]`
							: 'text-black lg:border-r-black',
					'transition-[border,_width,_shadow,_color] duration-500 ease-out md:duration-500',
					'border-r border-transparent duration-700 ease-in-out',

					open
						? 'min-w-screen w-fit text-brand-dusk !mix-blend-normal shadow-high-elevation sm:min-w-[250px]'
						: 'w-0 min-w-0 shadow-none md:w-navigation',
					open && (props.color === 'white' || props.color === 'adaptive')
						? '!border-black !bg-white'
						: 'bg-white',

					!open && (props.color === 'white' || props.color === 'adaptive')
						? 'bg-transparent md:group-hover:border-r-white'
						: isCustomColor
							? ``
							: '',

					isOnHomepage && open && '!border-white !bg-brand-dusk/80 !text-white',

					isOnHomepage && !open && 'bg-transparent',
				)}
				style={
					isCustomColor
						? open
							? {
									background: 'white',
									borderColor: 'black',
									color: 'black',
								}
							: mounted && isAtLeastTablet
								? {
										background: 'transparent',
										borderColor: props.color,
										color: props.color,
									}
								: {
										background: 'transparent',
										borderColor: 'transparent',
										color: props.color,
									}
						: undefined
				}
			>
				<div className="flex h-full flex-col items-start justify-between gap-sm">
					<Link
						viewTransition
						className={cn(
							open ? 'ml-md lg:ml-0' : 'mx-auto',
							'hidden w-[45px] md:block lg:w-[67px]',
						)}
						to={routes.public.base.route({})}
					>
						<LogoIcon.default />
					</Link>
					{/* Mobile Button */}
					<MenuButton
						className={cn(
							'pointer-events-auto ml-sm block md:hidden lg:ml-auto',
							open && '-mt-xxxs',
						)}
					/>

					{/* Central content */}
					<div
						className={cn(
							'rotate-180 flex-col items-center self-center font-bold [writing-mode:vertical-lr]',
							'text-center text-sm uppercase tracking-[0.2em]',
							open || !route.show || !route.content
								? 'hidden'
								: 'hidden lg:flex',
						)}
					>
						{isOnProductPage && (
							<div className="text-xs opacity-40">
								{[
									...searchParams.getAll('color'),
									...searchParams.getAll('typology'),
									...searchParams.getAll('width'),
									...searchParams.getAll('finish'),
								].join(' / ')}
							</div>
						)}
						<div className="flex items-center gap-2">
							{route.content.map((item, index, array) => (
								<Fragment key={item.label}>
									<div
										style={{
											opacity: 1 - index / array.length,
										}}
									>
										{item.to ? (
											<Link
												viewTransition
												className="cursor-pointer"
												to={item.to}
											>
												{item.label}
											</Link>
										) : (
											<p className="mb-0">{item.label}</p>
										)}
									</div>
									{index !== array.length - 1 && <p>/</p>}
								</Fragment>
							))}
						</div>
					</div>
					<div
						className={cn(
							'flex gap-md px-sm md:gap-xl',
							'select-none transition-all duration-200 ease-out',
							'text-sm uppercase tracking-[0.2em]',
							open ? 'w-fit opacity-100' : 'hidden w-0 opacity-0',
						)}
					>
						{/* Main menu */}
						<ul
							data-testid={'navigation-menu'}
							className="flex flex-col gap-sm"
						>
							{Object.entries(items).map(([key, value]) => (
								<li key={key}>
									{typeof value === 'string' ? (
										<LocalizedLink
											onClick={() => {
												toggle()
											}}
											className="uppercase"
											to={value}
										>
											{t(`common:to.${key}`)}.
										</LocalizedLink>
									) : (
										<button
											className={cn(
												'uppercase',
												selected === key && 'font-medium',
											)}
											type="button"
											onClick={() => {
												setSelected(key)
											}}
										>
											{t(`common:to.${key}`)}.
										</button>
									)}
								</li>
							))}
						</ul>
						{/* Submenu */}
						{selected && (
							<motion.ul
								className="flex flex-col gap-sm"
								variants={container}
								initial="hidden"
								animate="show"
							>
								{/* @ts-ignore */}
								{Object.entries(items[selected]).map(([key, value]) => (
									<motion.li key={key} variants={listItem}>
										<LocalizedLink
											onClick={() => {
												toggle()
											}}
											to={typeof value === 'string' ? value : value.route}
											className={
												typeof value === 'object' ? value.className : undefined
											}
										>
											{key}.
										</LocalizedLink>
									</motion.li>
								))}
							</motion.ul>
						)}
					</div>

					<div className={'flex w-full justify-between'}>
						<MenuButton className="pointer-events-auto hidden md:block" />
						{open && (
							<div className={'mx-sm'}>
								<SocialLinks />
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

function Header(
	{ theme }: { theme: 'light' | 'dark' | `#${string}` } = { theme: 'light' },
) {
	const { t } = useTranslation('common')

	const isCustomColor = theme.startsWith('#')

	return (
		<header
			style={
				isCustomColor
					? {
							color: theme,
						}
					: undefined
			}
			className={cn(
				'flex h-header-mobile max-w-[100vw] flex-wrap items-center justify-between gap-4 px-xxs sm:h-header-tablet sm:flex-nowrap sm:px-sm lg:h-header lg:px-md',
				theme === 'light' && 'text-white',
			)}
		>
			<div className="flex-1">
				<div className={'ml-xxl'}>
					<div className={'block lg:hidden'}>
						<SearchButton theme={theme} />
					</div>
				</div>
			</div>
			<Link viewTransition className="" to={routes.public.base.route({})}>
				<LogoFull.default className="hidden w-[250px] md:block lg:w-[300px] xl:w-[350px]" />
				<LogoIcon.default className="block w-[45px] sm:w-[67px] md:hidden" />
			</Link>
			<div className="flex flex-1 items-center gap-xxxs">
				<div className="ml-auto flex items-center gap-xxxs">
					{/*<SiteSearch status="idle" />*/}
					<div className={'hidden lg:block xl:mr-xxxl'}>
						<SearchButton theme={theme} />
					</div>
					<Button asChild variant={'ghost'} size={'sm'}>
						<LocalizedLink
							className={cn(
								'text-body-2xs lowercase tracking-[0.2em] text-current hover:underline md:text-body-xs',
								theme === 'light' && 'text-white',
							)}
							to={routes.public.contact.route({})}
						>
							<span className="hidden md:inline">{t('to.contact')}</span>
							<Icon
								name={'message-square-warning'}
								size={'md'}
								className="md:hidden"
							/>
						</LocalizedLink>
					</Button>
					<LanguageDropdown />
				</div>
			</div>
		</header>
	)
}

function SearchButton({ theme }: { theme: 'light' | 'dark' | `#${string}` }) {
	const { t } = useTranslation('common')

	return (
		<Button asChild variant="ghost" size="sm">
			<LocalizedLink
				className={cn(
					'text-body-2xs lowercase tracking-[0.2em] text-current hover:underline md:text-body-xs',
					theme === 'light' && 'text-white',
				)}
				to={routes.public.search.route({})}
			>
				<Icon
					name="magnifying-glass"
					size="md"
					className={'inline md:hidden'}
				/>
				<span className="hidden md:inline">{t('to.search')}</span>
			</LocalizedLink>
		</Button>
	)
}

// Extract legal links
const LegalLinks = memo(function LegalLinks({
	locale,
	t,
}: {
	locale: ISupportedLanguage
	t: (key: string) => string
}) {
	const user = useOptionalUser()

	return (
		<div
			className={
				'flex items-center gap-xs text-xs font-light text-brand-dusk/80 underline-offset-2 md:gap-3 md:text-center md:text-sm md:font-medium lg:justify-center'
			}
		>
			<a
				target={'_blank'}
				rel={'noopener noreferrer'}
				className={'underline'}
				href={routes.public.legal.cookies[locale]}
			>
				{t('root.cookiePolicy')}
			</a>
			<span className="text-brand-dusk/30">|</span>
			<LocalizedLink
				prefetch="intent"
				className={'underline'}
				to={routes.public.legal.privacy.route({})}
			>
				{t('root.privacyPolicy')}
			</LocalizedLink>
			<span className="text-brand-dusk/30">|</span>
			<LocalizedLink
				prefetch="intent"
				to={routes.public.auth.login.route({})}
				className={'underline'}
			>
				{user
					? user.roles.some((role) => role.name === 'admin')
						? t('to.admin')
						: t('to.privateArea')
					: t('to.login')}
			</LocalizedLink>
		</div>
	)
})

// Extract copyright section
const Copyright = memo(function Copyright({
	t,
}: {
	t: (key: string) => string
}) {
	const year = new Date().getFullYear()

	return (
		<p className="font-light tracking-normal">
			&copy; {year} Sapienstone - GranitiFiandre S.p.A | {t('footer.aBrandOf')}{' '}
			<a
				className="transition-all duration-300 ease-in-out hover:underline"
				href="https://www.irisceramicagroup.com"
			>
				Iris Ceramica Group
			</a>
		</p>
	)
})

// Extract animated word component to isolate re-renders
const AnimatedWord = memo(function AnimatedWord({
	currentWord,
	onWordClick,
}: {
	currentWord: number
	onWordClick: () => void
}) {
	const { t } = useTranslation('common')

	return (
		<div className="relative col-span-full">
			<p
				onClick={onWordClick}
				className="select-none font-serif text-[12vw] font-light lowercase leading-[0.85] tracking-normal lg:text-[10vw] xl:text-[11vw]"
			>
				We resolve
				<br /> your{' '}
				<motion.span
					key={wordList[currentWord]}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.4, ease: 'easeInOut' }}
				>
					{wordList[currentWord]}
				</motion.span>
			</p>

			<ProductLink
				link={routes.public.contact.route({})}
				children={t('to.contact')}
				className={cn(
					'border-brand-dusk/30 text-brand-dusk hover:border-brand-dusk hover:bg-brand-dusk/5',
					'!left-auto translate-x-[30vw]',
					'relative min-[450px]:!translate-x-[40vw]',
					'sm:!translate-x-[80%]',
					'md:absolute md:!translate-x-[60%]',
					'lg:!translate-x-[20%]',
					'2xl:!translate-x-full 2xl:!translate-y-[-10%]',
				)}
			/>
		</div>
	)
})

const wordList = ['doubts', 'needs', 'ideas']

const Footer = memo(function Footer() {
	const { t } = useTranslation('common')
	const locale = useLocale()

	const [currentWord, setCurrentWord] = useState(0)
	const lastChangeRef = useRef(Date.now())
	const canClickRef = useRef(true)

	// Single interval for word rotation
	useEffect(() => {
		const interval = setInterval(() => {
			const now = Date.now()

			if (now - lastChangeRef.current >= 2000) {
				setCurrentWord((prev) => (prev + 1) % wordList.length)
				lastChangeRef.current = now
				canClickRef.current = true
			}
		}, 100) // Check every 100ms instead of updating state every second

		return () => clearInterval(interval)
	}, [])

	const handleWordClick = useCallback(() => {
		const now = Date.now()

		// Debounce clicks to prevent rapid changes
		if (now - lastChangeRef.current < 1000 || !canClickRef.current) {
			return
		}

		setCurrentWord((prev) => (prev + 1) % wordList.length)
		lastChangeRef.current = now
		canClickRef.current = false

		// Re-enable clicking after 1 second
		setTimeout(() => {
			canClickRef.current = true
		}, 1000)
	}, [])

	return (
		<AnimatePresence mode={'wait'}>
			<div className="relative flex h-full flex-col justify-end overflow-hidden bg-background pt-[10vh] text-brand-dusk bg-blend-darken">
				<NewsletterForm id={'newsletter-form'} />

				<div className="relative z-20 mx-auto grid w-full max-w-[min(1560px,85vw)] gap-sm pt-xl text-sm font-medium tracking-[0.2em] md:px-sm md:pt-xl lg:pt-xxxl">
					<AnimatedWord
						currentWord={currentWord}
						onWordClick={handleWordClick}
					/>

					<div className="z-30 col-span-full grid gap-sm tracking-normal md:mt-xl lg:grid-cols-3">
						<Copyright t={t} />

						<LegalLinks locale={locale} t={t} />

						<div className="-order-1 flex items-center gap-sm md:order-none lg:justify-self-end xl:gap-lg">
							<SocialLinks />
						</div>
					</div>
				</div>

				<a
					href={'https://www.konam.dev'}
					className={
						'mx-auto mb-lg mt-md w-full max-w-[min(1560px,85vw)] text-xs font-light tracking-normal text-stone-400 md:text-center md:text-sm'
					}
				>
					{t('footer.developedBy')} Konam
				</a>
			</div>
		</AnimatePresence>
	)
})

function LanguageDropdown() {
	const { t, i18n } = useTranslation()

	const onValueChange = (lang: string) => {
		// Create a form and submit it - this will cause a full page reload
		// The server action will set the cookie and redirect to the correct URL
		const form = document.createElement('form')
		form.method = 'POST'
		form.action = `/resources/change-language/${lang}`
		document.body.appendChild(form)
		form.submit()
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					className={'tracking-[0.2em]'}
					data-testid={'language-selector'}
					variant="ghost"
					size="sm"
				>
					{i18n.language === 'es'
						? 'esp'
						: i18n.language === 'en'
							? 'eng'
							: i18n.language === 'fr'
								? 'fra'
								: i18n.language === 'it'
									? 'ita'
									: 'deu'}
					/
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<DropdownMenuRadioGroup
					value={i18n.language}
					onValueChange={onValueChange}
				>
					<DropdownMenuRadioItem value="es">
						{t('root.language.spanish')}
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem value="en">
						{t('root.language.english')}
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem value="fr">
						{t('root.language.french')}
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem value="de">
						{t('root.language.german')}
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem value="it">
						{t('root.language.italian')}
					</DropdownMenuRadioItem>
				</DropdownMenuRadioGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
